import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import FormSectionNavigator from '../../../components/ui/FormSectionNavigator';
import GuestBookForm from './GuestBookForm';
import VolunteerForm from './VolunteerForm';
// import ArtSubmissionForm from './ArtSubmissionForm';

const EngagementFormsSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [activeForm, setActiveForm] = useState('guestbook');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'ar';
    setCurrentLanguage(savedLanguage);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'ar';
      setCurrentLanguage(newLanguage);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const sectionContent = {
    ar: {
      title: "سجل للمشاركة",
      subtitle: "احضر يوم الذكرى الأولى للتحرير",
      description: "التسجيل فردي، يرجى من أفراد العوائل التسجيل كل فرد على حدة",
      disclaimer: "تنويه: هذا النموذج مخصّص للتسجيل لحضور فعالية إسطنبول فقط.",
      disclaimerLine2: "إذا كنت ترغب بالحضور من ولاية أخرى، يرجى الانتقال إلى قسم الولايات.",
      statesLink: "التسجيل من ولاية أخرى"
    },
    en: {
      title: "Register to Participate",
      subtitle: "Attend the First Anniversary of Liberation Day",
      description: "Registration is individual. Please have family members register each person separately.",
      disclaimer: "Notice: This form is for registration to attend the Istanbul event only.",
      disclaimerLine2: "If you wish to attend from another state, please go to the States section.",
      statesLink: "Register from Another State"
    },
    tr: {
      title: "Katılmak İçin Kayıt Olun",
      subtitle: "Kurtuluşun İlk Yıldönümüne Katılın",
      description: "Kayıt bireyseldir. Lütfen aile üyelerinin her birinin ayrı ayrı kayıt olmasını sağlayın.",
      disclaimer: "Uyarı: Bu form yalnızca İstanbul etkinliğine katılmak için kayıt içindir.",
      disclaimerLine2: "Başka bir ilden katılmak isterseniz, lütfen İller bölümüne gidin.",
      statesLink: "Başka Bir İlden Kayıt Olun"
    }
  };

  const renderActiveForm = () => {
    switch (activeForm) {
      case 'volunteer':
        return <VolunteerForm />;
      // case 'partner':
      //   return <ArtSubmissionForm />;
      default:
        return <GuestBookForm />;
    }
  };

  const scrollToStates = () => {
    const element = document.getElementById('states');
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const content = sectionContent?.[currentLanguage];

  return (
    <section id="get-involved" className="py-16 lg:py-24 bg-surface">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="font-memorial text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
            {content?.title}
          </h2>
          <p className="font-body text-xl text-accent mb-3">
            {content?.subtitle}
          </p>
          <p className="font-body text-base md:text-lg text-text-secondary max-w-3xl mx-auto mb-6">
            {content?.description}
          </p>

          {/* Disclaimer Notice */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-accent/5 border border-accent/20 rounded-lg p-4 max-w-2xl mx-auto"
          >
            <p className="text-sm text-text-secondary mb-2">
              <strong>{content?.disclaimer}</strong>
            </p>
            <p className="text-sm text-text-secondary mb-3">
              {content?.disclaimerLine2}
            </p>
            <button
              onClick={scrollToStates}
              className="text-primary hover:text-primary-dark font-semibold text-sm underline transition-colors"
            >
              👉 {content?.statesLink}
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="bg-card border border-border rounded-lg shadow-memorial overflow-hidden"
        >
          {/* Form Navigator */}
          <div className="border-b border-border">
            <FormSectionNavigator
              activeForm={activeForm}
              onFormChange={setActiveForm}
            />
          </div>

          {/* Active Form */}
          <div className="p-6 md:p-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeForm}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderActiveForm()}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default EngagementFormsSection;